import { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { useViewer } from '~/features/auth'

/**
 * The landing page's call to action, in the one shape it has.
 *
 * There are two of these on the page — the hero button and the one that closes Pick A Side — and
 * they had drifted into different objects: one `rounded`, one `rounded-none`; 13em wide against
 * 14em; the padding on the button in one and on the link inside it in the other. Same job, same
 * page, two shapes.
 *
 * It is a link, not a `GradientButton`. Both call sites were rendering a `NavLink` inside
 * `GradientButton`'s `<button>`, which is an anchor nested in a button — invalid, and it makes the
 * hit area depend on which of the two got the padding. Applying `.button-gradient` (and its blurred
 * `::after`) straight to the anchor gives the same object with the whole box clickable. The
 * utilities out-specify the class's own `padding: 0.25em 3em` because Tailwind emits components
 * before utilities.
 */
function LandingCta({
    to,
    children,
    animate = false,
    className = '',
}: {
    to: string
    children: ReactNode
    animate?: boolean
    className?: string
}) {
    return (
        <NavLink
            to={to}
            className={twMerge(
                'button-gradient flex min-h-[3rem] min-w-[14em] select-none items-center justify-center rounded-none px-[1.5em] py-[0.75em] text-center font-bold uppercase tracking-wide transition-transform hover:scale-[1.02] text-[clamp(0.875rem,1.4vw,1.5rem)]',
                animate ? 'button-gradient-animate' : '',
                className
            )}
        >
            {children}
        </NavLink>
    )
}

/**
 * The hero. Signed in it points at the download, signed out at signup.
 *
 * The animated gradient is the one thing it does not share with the Pick A Side button — this is
 * the page's primary call to action and is allowed to move.
 */
export function HeroButton() {
    const { data: user, isFetched } = useViewer()
    if (!isFetched) return null

    return (
        <div className='flex flex-row justify-center px-4 my-6 md:my-8'>
            <LandingCta animate to={user?.is_auth ? '/download' : '/signup'}>
                {user?.is_auth ? 'DOWNLOAD NOW' : 'GET STARTED'}
            </LandingCta>
        </div>
    )
}

/**
 * Closes the Pick A Side section.
 *
 * Signed out this stays GET STARTED to /signup: /store is behind `RequireAuth`, so pointing a
 * visitor with no account at a shop they cannot open would spend the section's one call to action
 * on a login redirect.
 */
export function ShopButton({ className = '' }: { className?: string }) {
    const { data: user } = useViewer()

    return (
        <LandingCta to={user?.is_auth ? '/store' : '/signup'} className={className}>
            {user?.is_auth ? 'EXPLORE SHOP' : 'GET STARTED'}
        </LandingCta>
    )
}
